import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import RedesignedHeader from "./components/RedesignedHeader";
import RedesignedHero from "./components/RedesignedHero";
import RedesignedServices from "./components/RedesignedServices";
import RedesignedIndustries from "./components/RedesignedIndustries";
import RedesignedCaseStudies from "./components/RedesignedCaseStudies";
import RedesignedUseCases from "./components/RedesignedUseCases";
import Footer from "./components/Footer";
import Chat from "./components/Chat";
import EIEDemo from "./components/EIEDemo";
import EmotionSmokeOverlay from "./components/EmotionSmokeOverlay";
import "./Cinematic.css";
import "./App.css";

const EMOTION_THEMES = {
    happy: { theme: "happy", accent: "#F59E0B" },
    neutral: { theme: "neutral", accent: "#8B5CF6" },
    yawn: { theme: "yawn", accent: "#84905E" },
    sad: { theme: "clarity", accent: "#2563EB" },
    angry: { theme: "passion", accent: "#F50000" },
    surprise: { theme: "surprise", accent: "#FF5A36" },
    focused: { theme: "focused", accent: "#06B6D4" },
    passion: { theme: "passion", accent: "#F50000" },
    clarity: { theme: "clarity", accent: "#1d4ed8" },
};

function App() {
    const [theme, setTheme] = useState(() => Math.random() < 0.5 ? 'passion' : 'clarity');
    const [heroStartSignal, setHeroStartSignal] = useState(0);
    const [isDemoRunning, setIsDemoRunning] = useState(false);
    const overlayRef = useRef(null);
    const isDemoRunningRef = useRef(false);
    const lastManualThemeRef = useRef(theme);
    const lastEmotionThemeAtRef = useRef(0);
    const lastEmotionLabelRef = useRef("");
    const lastEmotionBucketRef = useRef("");
    const shiftTimerRef = useRef(null);
    const eieWidgetConfig = useMemo(() => ({
        localClassifier: {
            calibrationDuration: Number(import.meta.env.VITE_EIE_CALIBRATION_MS) || 1200,
        },
        heartRate: {
            enabled: true,
            calibrationDuration: Number(import.meta.env.VITE_EIE_HR_CALIBRATION_S) || 60,
        },
    }), []);

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
        if (!isDemoRunningRef.current) {
            lastManualThemeRef.current = theme;
        }
    }, [theme]);

    useEffect(() => {
        document.documentElement.setAttribute('data-eie-live', 'false');
        return () => {
            document.documentElement.removeAttribute('data-eie-live');
        };
    }, []);

    const commitThemeShift = useCallback((newTheme) => {
        if (shiftTimerRef.current) clearTimeout(shiftTimerRef.current);
        document.documentElement.setAttribute('data-theme-shifting', '');
        setTheme(newTheme);
        shiftTimerRef.current = setTimeout(() => {
            document.documentElement.removeAttribute('data-theme-shifting');
            shiftTimerRef.current = null;
        }, 1400);
    }, []);

    const runThemeSmokeTransition = useCallback((themeKey) => {
        const info = EMOTION_THEMES[themeKey] || EMOTION_THEMES.neutral;
        overlayRef.current?.triggerSmoke(info.accent, info.theme, () => {
            commitThemeShift(info.theme);
        });
    }, [commitThemeShift]);

    const toggleTheme = () => {
        if (isDemoRunningRef.current) return;
        setTheme(prev => prev === 'passion' ? 'clarity' : 'passion');
    };

    const handleEmotionTheme = useCallback((bucket, options = {}) => {
        if (!isDemoRunningRef.current) return;
        const force = options?.force === true;
        const emotionLabel = typeof options?.emotionLabel === 'string' && options.emotionLabel.trim()
            ? options.emotionLabel.trim()
            : bucket;
        const now = Date.now();
        const isDuplicateLabel = emotionLabel === lastEmotionLabelRef.current;
        const isDifferentEmotion = bucket !== lastEmotionBucketRef.current;

        // Only show caption if emotion is different from current one
        if (!isDifferentEmotion) return;

        // Block duplicate labels within 2600ms
        if (isDuplicateLabel && now - lastEmotionThemeAtRef.current < 2600) return;
        // Different emotions can transition immediately without cooldown
        // (isDifferentEmotion is already true at this point)

        lastEmotionThemeAtRef.current = now;
        lastEmotionLabelRef.current = emotionLabel;
        lastEmotionBucketRef.current = bucket;
        const info = EMOTION_THEMES[bucket] || EMOTION_THEMES.neutral;
        overlayRef.current?.triggerSmoke(info.accent, info.theme, () => {
            commitThemeShift(info.theme);
        }, emotionLabel);
    }, []);

    const handleDemoStart = useCallback(() => {
        isDemoRunningRef.current = true;
        setIsDemoRunning(true);
        document.documentElement.setAttribute('data-eie-live', 'true');
        lastEmotionThemeAtRef.current = 0;
        lastEmotionLabelRef.current = "";
        lastEmotionBucketRef.current = "";
        lastManualThemeRef.current = theme;
    }, [theme]);

    const handleDemoStop = useCallback(() => {
        isDemoRunningRef.current = false;
        setIsDemoRunning(false);
        document.documentElement.setAttribute('data-eie-live', 'false');
        lastEmotionThemeAtRef.current = 0;
        lastEmotionLabelRef.current = "";
        lastEmotionBucketRef.current = "";
        runThemeSmokeTransition(lastManualThemeRef.current);
    }, [runThemeSmokeTransition]);

    const handleHeroPlay = useCallback(() => {
        setHeroStartSignal((prev) => prev + 1);
    }, []);

    const currentAccent = EMOTION_THEMES[theme]?.accent || EMOTION_THEMES.neutral.accent;

    return (
        <div className="redesigned-app">
            <EmotionSmokeOverlay ref={overlayRef} />
            <RedesignedHeader onThemeToggle={toggleTheme} currentTheme={theme} />
            <RedesignedHero onPlayWithEIE={handleHeroPlay} isPlayDisabled={isDemoRunning} />
            <RedesignedServices />
            <RedesignedIndustries />
            <RedesignedCaseStudies />
            <RedesignedUseCases />
            <EIEDemo
                onEmotionTheme={handleEmotionTheme}
                onDemoStart={handleDemoStart}
                onDemoStop={handleDemoStop}
                heroStartSignal={heroStartSignal}
                badgeAccent={currentAccent}
                widgetConfig={eieWidgetConfig}
            />
            <Footer />
            <Chat />
        </div>
    );
}

export default App;
